import { HandCoins } from "lucide-react";
import { useEffect, useState } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

/**
 * Small pill displaying a broker's co-broker recommendation score (0-100).
 * Green when ≥75, blue otherwise. Includes a tooltip explaining what the
 * score means.
 *
 * Accepts either:
 *   - `score` prop (already-computed integer 0-100) — preferred when
 *     available inline in payloads to avoid extra fetches.
 *   - `brokerId` prop — falls back to fetching `/api/brokers/{id}/score`.
 */
export function CoBrokerScorePill({ score: initialScore, brokerId, className }) {
  const [score, setScore] = useState(
    typeof initialScore === "number" ? initialScore : null
  );

  useEffect(() => {
    if (typeof initialScore === "number") {
      setScore(initialScore);
      return;
    }
    if (!brokerId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/brokers/${brokerId}/score`);
        if (!cancelled) setScore(data.score);
      } catch (e) {
        // Soft signal — swallow error but log for diagnostics.
        console.error("[CoBrokerScorePill] score fetch failed:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [brokerId, initialScore]);

  if (score == null) return null;
  const isStrong = score >= 75;
  const color = isStrong ? "#35622F" : "#1B2B4B";

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold",
              className
            )}
            style={{
              borderColor: `${color}40`,
              backgroundColor: `${color}12`,
              color,
            }}
            data-testid="co-broker-score-pill"
            data-score={score}
          >
            <HandCoins className="h-3 w-3" />
            Co-broker {score}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-xs rounded-xl border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 shadow-lg"
        >
          A network-trust score (0–100) based on verification, active
          listings, deals in flight, closings, and dispute history.
          <br />
          {isStrong ? "Strong track record." : "Standard reputation."}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
